/**
 * Inline SVG icons — ported 1:1 from the source markup (stroke paths + widths).
 * Each takes { size, color, strokeWidth } and renders via react-native-svg.
 */
import React from 'react';
import Svg, { Circle, Path, Rect } from 'react-native-svg';

type IconProps = { size?: number; color?: string; strokeWidth?: number };
const base = (size = 16) => ({ width: size, height: size, viewBox: '0 0 24 24', fill: 'none' as const });

export const Plus = ({ size = 15, color = 'currentColor', strokeWidth = 2.4 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round"><Path d="M12 5v14M5 12h14" /></Svg>
);

export const Search = ({ size = 15, color = 'currentColor', strokeWidth = 2 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth}><Circle cx="11" cy="11" r="7" /><Path d="m21 21-4.3-4.3" /></Svg>
);

export const ChevronRight = ({ size = 16, color = 'currentColor', strokeWidth = 2 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round"><Path d="m9 18 6-6-6-6" /></Svg>
);

export const ChevronDown = ({ size = 11, color = 'currentColor', strokeWidth = 2.6 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth}><Path d="m6 9 6 6 6-6" /></Svg>
);

export const ChevronLeft = ({ size = 16, color = 'currentColor', strokeWidth = 2.2 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><Path d="m15 18-6-6 6-6" /></Svg>
);

export const ArrowRight = ({ size = 14, color = 'currentColor', strokeWidth = 2.4 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><Path d="M5 12h14" /><Path d="m13 6 6 6-6 6" /></Svg>
);

export const Check = ({ size = 14, color = 'currentColor', strokeWidth = 2.5 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><Path d="M20 6 9 17l-5-5" /></Svg>
);

export const Close = ({ size = 18, color = 'currentColor', strokeWidth = 2.2 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth}><Path d="M18 6 6 18M6 6l12 12" /></Svg>
);

export const Sparkles = ({ size = 11, color = 'currentColor', strokeWidth = 2.2 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round"><Path d="M12 3v3M12 18v3M3 12h3M18 12h3M5.6 5.6l2.1 2.1M16.3 16.3l2.1 2.1M18.4 5.6l-2.1 2.1M7.7 16.3l-2.1 2.1" /></Svg>
);

export const Warning = ({ size = 16, color = 'currentColor', strokeWidth = 2.2 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth}><Path d="M12 9v4M12 17h.01" /><Path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></Svg>
);

export const WarningCircle = ({ size = 16, color = 'currentColor', strokeWidth = 2.2 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth}><Path d="M12 9v4M12 17h.01" /><Circle cx="12" cy="12" r="10" /></Svg>
);

export const Cube = ({ size = 19, color = 'currentColor', strokeWidth = 2.1 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><Path d="M12 2 2 7l10 5 10-5-10-5z" /><Path d="m2 17 10 5 10-5M2 12l10 5 10-5" /></Svg>
);

export const Brackets = ({ size = 19, color = 'currentColor', strokeWidth = 2.1 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><Path d="m16 18 6-6-6-6M8 6l-6 6 6 6" /></Svg>
);

export const Lock = ({ size = 11, color = 'currentColor', strokeWidth = 2.4 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth}><Rect x="3" y="11" width="18" height="11" rx="2" /><Path d="M7 11V7a5 5 0 0 1 10 0v4" /></Svg>
);

export const Chart = ({ size = 14, color = 'currentColor', strokeWidth = 2 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><Path d="M4 4v16h16" /><Path d="m4 15 4-4 4 3 6-7" /></Svg>
);

export const Gear = ({ size = 13, color = 'currentColor', strokeWidth = 2.2 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><Circle cx="12" cy="12" r="3" /><Path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></Svg>
);

export const Pencil = ({ size = 13, color = 'currentColor', strokeWidth = 2.2 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><Path d="M12 20h9" /><Path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></Svg>
);

export const Copy = ({ size = 14, color = 'currentColor', strokeWidth = 2 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><Rect x="9" y="9" width="13" height="13" rx="2" /><Path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" /></Svg>
);

export const Refresh = ({ size = 14, color = 'currentColor', strokeWidth = 2 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><Path d="M21 12a9 9 0 1 1-2.6-6.4" /><Path d="M21 3v5h-5" /></Svg>
);

export const Trash = ({ size = 14, color = 'currentColor', strokeWidth = 2.2 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round"><Path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /></Svg>
);

export const Swap = ({ size = 20, color = 'currentColor', strokeWidth = 2.2 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round"><Path d="m17 1 4 4-4 4" /><Path d="M3 11V9a4 4 0 0 1 4-4h14" /><Path d="m7 23-4-4 4-4" /><Path d="M21 13v2a4 4 0 0 1-4 4H3" /></Svg>
);

export const Star = ({ size = 15, color = 'currentColor', fill = 'none', strokeWidth = 1.8 }: IconProps & { fill?: string }) => (
  <Svg {...base(size)} fill={fill} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><Path d="m12 2 3 6.3 6.9 1-5 4.9 1.2 6.8L12 17.8 5.9 21l1.2-6.8-5-4.9 6.9-1z" /></Svg>
);

export const Upload = ({ size = 20, color = 'currentColor', strokeWidth = 2 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round"><Path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><Path d="M7 10l5-5 5 5" /><Path d="M12 5v12" /></Svg>
);

export const Layers = ({ size = 13, color = 'currentColor', strokeWidth = 2.2 }: IconProps) => (
  <Svg {...base(size)} stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"><Path d="M12 2 2 7l10 5 10-5-10-5z" /><Path d="m2 17 10 5 10-5M2 12l10 5 10-5" /></Svg>
);

export { default as BridgeMark } from './BridgeMark';
